/**
 * Endpoint Store - Global state management for endpoints
 * Provides a singleton store for managing available endpoints and selected endpoint
 */

import { 
  storeEndpointsList as persistEndpointsList, 
  getStoredEndpointsList,
  storeConfigEndpoint,
  getStoredConfigEndpoint
} from './configEndpointManager';

// In-memory cache
let cachedEndpoints = [];
let cachedSelectedEndpoint = null;
let isInitialized = false;

// Initialize from localStorage
const initializeFromStorage = () => {
  if (isInitialized) return;

  cachedEndpoints = getStoredEndpointsList();
  const storedConfigEndpoint = getStoredConfigEndpoint();

  if (cachedEndpoints.length > 0 && storedConfigEndpoint) {
    cachedSelectedEndpoint = storedConfigEndpoint;
    console.log('[EndpointStore] Initialized from storage:', {
      endpointsCount: cachedEndpoints.length,
      selectedEndpoint: cachedSelectedEndpoint
    });
  }

  isInitialized = true;
};

// Initialize on module load
initializeFromStorage();

// Event listener for storage changes
window.addEventListener('storage', (e) => {
  if (e.key === 'public-dns-endpoints-list' || e.key === 'config-service-endpoint') {
    cachedEndpoints = getStoredEndpointsList();
    const storedConfigEndpoint = getStoredConfigEndpoint();
    if (storedConfigEndpoint) {
      cachedSelectedEndpoint = storedConfigEndpoint;
    }
    // Notify subscribers
    window.dispatchEvent(new Event('endpoint-store-updated'));
  }
});

/**
 * Get all available endpoints
 */
export const getAllEndpoints = () => {
  if (!isInitialized) initializeFromStorage();
  return [...cachedEndpoints];
};

/**
 * Get the currently selected endpoint
 */
export const getSelectedEndpointUrl = () => {
  if (!isInitialized) initializeFromStorage();
  
  // If we have a cached selected endpoint, return it
  if (cachedSelectedEndpoint) {
    return cachedSelectedEndpoint;
  }
  
  // Otherwise, get from config endpoint manager
  const configEndpoint = getStoredConfigEndpoint();
  if (configEndpoint) {
    cachedSelectedEndpoint = configEndpoint;
    return configEndpoint;
  }
  
  // Fallback to first endpoint in list
  if (cachedEndpoints.length > 0) {
    const first = cachedEndpoints[0].baseUrl || cachedEndpoints[0].url || '';
    cachedSelectedEndpoint = first;
    return first;
  }
  
  return null;
};

/**
 * Get endpoints as options for dropdown (format: { value, label })
 */
export const getEndpointOptions = () => {
  const endpoints = getAllEndpoints();
  return endpoints.map((ep, index) => ({
    value: ep.baseUrl || ep.url || '',
    label: `${ep.baseUrl || ep.url || `Endpoint ${index + 1}`}${ep.name ? ` (${ep.name})` : ''}`,
    ...ep
  }));
};

/**
 * Set the selected endpoint
 * @param {string|Object} endpoint - Endpoint URL string or endpoint object with baseUrl/url property
 */
export const setSelectedEndpoint = (endpoint) => {
  if (!endpoint) {
    console.warn('[EndpointStore] Empty endpoint provided');
    return;
  }

  // Extract URL from endpoint object if needed
  const endpointUrl = typeof endpoint === 'object'
    ? endpoint?.baseUrl || endpoint?.url || ''
    : endpoint;

  if (!endpointUrl) {
    console.warn('[EndpointStore] No URL found in endpoint object');
    return;
  }

  // Store the endpoint with detailed logging
  console.log('[EndpointStore] Storing config endpoint:', endpointUrl);
  
  // Try multiple storage strategies
  let storageSuccessful = false;
  
  // Strategy 1: Direct write to 'config-service-endpoint'
  try {
    localStorage.setItem('config-service-endpoint', endpointUrl);
    console.log('[EndpointStore] ✅ Direct write to config-service-endpoint SUCCESS');
    storageSuccessful = true;
  } catch (err) {
    console.error('[EndpointStore] ❌ Direct write to config-service-endpoint FAILED:', err);
  }
  
  // Strategy 2: DO NOT overwrite 'admin-backend-endpoint' - it should remain as the auth service URL
  // The admin-backend-endpoint is set during login when user selects WEB2/WEB3
  // and should remain as the auth service URL for Admin Management
  console.log('[EndpointStore] ℹ️ Skipping write to admin-backend-endpoint to preserve auth service URL');
  
  // Strategy 3: Verify the write worked
  setTimeout(() => {
    const storedDirect = localStorage.getItem('config-service-endpoint');
    const storedAuthEndpoint = localStorage.getItem('admin-backend-endpoint');
    console.log('[EndpointStore] Verification:', {
      'config-service-endpoint': storedDirect,
      'admin-backend-endpoint (auth service)': storedAuthEndpoint
    });
    
    if (storedDirect === endpointUrl) {
      console.log('[EndpointStore] ✅ Config service endpoint storage verified!');
    } else {
      console.error('[EndpointStore] ❌ Config service endpoint storage failed!');
    }
  }, 100);

  // Store in cached state
  cachedSelectedEndpoint = endpointUrl;

  // Dispatch event for subscribers
  window.dispatchEvent(new Event('endpoint-selected'));
};

/**
 * Store endpoints list (called after fetching from auth service)
 */
export const storeEndpoints = (endpoints) => {
  if (!Array.isArray(endpoints)) {
    console.warn('[EndpointStore] Invalid endpoints array provided');
    return;
  }
  
  cachedEndpoints = [...endpoints];
  persistEndpointsList(endpoints);
  
  console.log('[EndpointStore] Endpoints stored:', endpoints.length, 'endpoints');
  
  // If no endpoint is selected, select the first one
  if (!cachedSelectedEndpoint && endpoints.length > 0) {
    const first = endpoints[0].baseUrl || endpoints[0].url || '';
    if (first) {
      setSelectedEndpoint(first);
    }
  }
};

/**
 * Check if we have any endpoints
 */
export const hasEndpoints = () => {
  if (!isInitialized) initializeFromStorage();
  return cachedEndpoints.length > 0 || !!getStoredConfigEndpoint();
};

/**
 * Clear all endpoints (on logout)
 * NOTE: Does NOT clear 'admin-backend-endpoint' as it should persist as the auth service URL
 */
export const clearEndpoints = () => {
  cachedEndpoints = [];
  cachedSelectedEndpoint = null;
  localStorage.removeItem('public-dns-endpoints-list');
  localStorage.removeItem('config-service-endpoint');
  // DO NOT remove 'admin-backend-endpoint' - it should persist as the auth service URL
  console.log('[EndpointStore] Config endpoints cleared (auth service endpoint preserved)');
  window.dispatchEvent(new Event('endpoint-store-cleared'));
};

/**
 * Get endpoint count
 */
export const getEndpointCount = () => {
  if (!isInitialized) initializeFromStorage();
  return cachedEndpoints.length;
};

export default {
  getAllEndpoints,
  getSelectedEndpointUrl,
  getEndpointOptions,
  setSelectedEndpoint,
  storeEndpoints,
  hasEndpoints,
  clearEndpoints,
  getEndpointCount
};
